/* Journal-Ready Figure Quality Checker for Physiology views.
   Client-side rule-based audit of Recharts/data-driven figures.
   Reports: axis labels, legend, data sufficiency, outliers, scale issues — flags issues before export.
*/
import React, { useMemo, useState } from 'react';
import { ShieldCheck, ShieldAlert, ShieldX, ChevronDown, ChevronUp } from 'lucide-react';

const TEAL = '#0F766E';
const GREEN_OK = '#059669';
const AMBER_WARN = '#D97706';
const RED_FAIL = '#DC2626';

/* Heuristic audits applied to each figure descriptor.
   A figure descriptor: { id, title, type, dataPoints, xLabel, yLabel, hasLegend, categories, values, unit } */
function auditFigure(fig) {
  const issues = [];
  const warnings = [];
  const ok = [];

  // Data sufficiency
  if (!fig.dataPoints || fig.dataPoints < 3) {
    issues.push('Too few data points (<3) — unreliable summary.');
  } else if (fig.dataPoints < 5) {
    warnings.push(`Only ${fig.dataPoints} data points — consider more replication.`);
  } else {
    ok.push(`${fig.dataPoints} data points — sufficient.`);
  }

  // Axis labels with units
  if (fig.type !== 'radar' && fig.type !== 'table') {
    if (!fig.xLabel) issues.push('Missing X-axis label.');
    else if (!/\(.+\)|[a-zA-Z]\/[a-zA-Z]|µ|%|°|,/.test(fig.xLabel)) {
      warnings.push(`X-axis label "${fig.xLabel}" has no units in parentheses.`);
    } else ok.push('X-axis labelled with units.');

    if (!fig.yLabel) issues.push('Missing Y-axis label.');
    else if (!/\(.+\)|[a-zA-Z]\/[a-zA-Z]|µ|%|°|,/.test(fig.yLabel)) {
      warnings.push(`Y-axis label "${fig.yLabel}" has no units in parentheses.`);
    } else ok.push('Y-axis labelled with units.');
  }

  // Legend presence for multi-series
  if (fig.multiSeries && !fig.hasLegend) {
    issues.push('Multi-series plot without legend.');
  } else if (fig.multiSeries) {
    ok.push('Legend present for multi-series plot.');
  }

  // Value range sanity
  if (fig.values && fig.values.length) {
    const mn = Math.min(...fig.values);
    const mx = Math.max(...fig.values);
    if (mx === mn) issues.push('All values identical — no variability to plot.');
    else if (mx > 0 && mn > 0 && (mx / mn > 1000)) {
      warnings.push('Values span 3+ orders of magnitude — consider log scale.');
    } else ok.push('Value range sensible.');
  }

  // Publication DPI hint
  ok.push('Chart renders as vector SVG — effective DPI: unlimited (scales to any journal requirement).');

  const grade = issues.length > 0 ? 'fail' : (warnings.length > 0 ? 'warn' : 'ok');
  return { ...fig, issues, warnings, ok, grade };
}

/* Build figure descriptors from a result dict. Each physio view passes a
   figures[] array describing its Recharts plots so we can audit them. */
export function buildPhysioFigures(analysisType, data) {
  if (!data) return [];
  const figs = [];
  const push = (f) => figs.push(auditFigure({ type: 'line', ...f }));

  if (analysisType === 'physio_crop_growth') {
    push({
      id: 'biomass-curve', title: 'Biomass Growth Curve',
      dataPoints: (data.progress_curves || []).length,
      xLabel: 'Days After Sowing', yLabel: 'Dry weight (g)',
      hasLegend: true, multiSeries: true,
      values: (data.progress_curves || []).map(r => r.Dry_weight).filter(v => v != null),
    });
    push({
      id: 'rgr-bar', title: 'RGR Across Intervals', type: 'bar',
      dataPoints: (data.growth_indices_table || []).length,
      xLabel: 'Interval', yLabel: 'RGR (g/g/day)',
      hasLegend: true, multiSeries: true,
      values: (data.growth_indices_table || []).map(r => r.RGR_g_g_day).filter(v => v != null),
    });
  } else if (analysisType === 'physio_photosynthesis') {
    push({
      id: 'pn-summary', title: 'Gas Exchange Summary', type: 'table',
      dataPoints: (data.summary_table || []).length,
      hasLegend: false, multiSeries: false,
    });
  } else if (analysisType === 'physio_water_relations') {
    push({
      id: 'sti-biplot', title: 'STI vs Mean Yield Biplot', type: 'scatter',
      dataPoints: (data.stress_indices_table || []).length,
      xLabel: 'Mean Yield (t/ha)', yLabel: 'STI',
      hasLegend: false, multiSeries: false,
      values: (data.stress_indices_table || []).map(r => r.STI).filter(v => v != null),
    });
  } else if (analysisType === 'physio_oxidative_stress') {
    push({
      id: 'mda-chart', title: 'MDA Bar Chart', type: 'bar',
      dataPoints: (data.stress_markers_table || []).length,
      xLabel: 'Genotype', yLabel: 'MDA (nmol/g FW)',
      hasLegend: true, multiSeries: true,
      values: (data.stress_markers_table || []).map(r => r.MDA).filter(v => v != null),
    });
    push({
      id: 'proline-chart', title: 'Proline Bar Chart', type: 'bar',
      dataPoints: (data.stress_markers_table || []).length,
      xLabel: 'Genotype', yLabel: 'Proline (µg/g FW)',
      hasLegend: true, multiSeries: true,
      values: (data.stress_markers_table || []).map(r => r.Proline).filter(v => v != null),
    });
  } else if (analysisType === 'physio_enzyme_activity') {
    push({
      id: 'enzyme-bar', title: 'Enzyme Activities', type: 'bar',
      dataPoints: (data.enzyme_activity_table || []).length,
      xLabel: 'Treatment', yLabel: 'Activity (µmol/min/mg protein)',
      hasLegend: true, multiSeries: true,
      values: [],
    });
  } else if (analysisType === 'physio_integrated_tolerance') {
    push({
      id: 'tolerance-radar', title: '4-D Tolerance Radar', type: 'radar',
      dataPoints: (data.top5_radar || []).length,
      hasLegend: true, multiSeries: true,
    });
  } else if (analysisType === 'physio_biofortification') {
    push({
      id: 'biofort-bar', title: 'Biofortification Score', type: 'bar',
      dataPoints: (data.biofort_ranking || []).length,
      xLabel: 'Genotype', yLabel: 'Biofortification score (0-100)',
      hasLegend: false, multiSeries: false,
      values: (data.biofort_ranking || []).map(r => r.Biofortification_score_0_100).filter(v => v != null),
    });
    push({
      id: 'biofort-radar', title: 'Nutrient Profile Radar', type: 'radar',
      dataPoints: (data.top5_radar || []).length,
      hasLegend: true, multiSeries: true,
    });
  }
  return figs;
}

function Badge({ grade, children }) {
  const map = {
    ok: { bg: '#ECFDF5', fg: GREEN_OK, Icon: ShieldCheck, label: 'Journal-ready' },
    warn: { bg: '#FEF3C7', fg: AMBER_WARN, Icon: ShieldAlert, label: 'Minor issues' },
    fail: { bg: '#FEE2E2', fg: RED_FAIL, Icon: ShieldX, label: 'Needs fixing' },
  };
  const { bg, fg, Icon, label } = map[grade] || map.ok;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ background: bg, color: fg }}>
      <Icon size={11} /> {children || label}
    </span>
  );
}

export default function PhysioFigureQualityChecker({ analysisType, data }) {
  const [open, setOpen] = useState(false);
  const figures = useMemo(() => buildPhysioFigures(analysisType, data), [analysisType, data]);
  if (!figures.length) return null;

  const okCount = figures.filter(f => f.grade === 'ok').length;
  const warnCount = figures.filter(f => f.grade === 'warn').length;
  const failCount = figures.filter(f => f.grade === 'fail').length;

  const overall = failCount > 0 ? 'fail' : (warnCount > 0 ? 'warn' : 'ok');

  return (
    <div className="rounded-xl bg-white border border-teal-200 overflow-hidden" data-testid="physio-figure-quality">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-3 hover:bg-teal-50 transition"
        data-testid="physio-fq-toggle"
      >
        <div className="flex items-center gap-3">
          <Badge grade={overall}>
            {overall === 'ok' ? `All ${figures.length} figures journal-ready` :
             overall === 'warn' ? `${warnCount} warning${warnCount !== 1 ? 's' : ''} · ${okCount} OK` :
             `${failCount} critical issue${failCount !== 1 ? 's' : ''} · ${warnCount} warn · ${okCount} OK`}
          </Badge>
          <span className="text-xs text-gray-500">Figure Quality Checker — {figures.length} figure{figures.length !== 1 ? 's' : ''} audited</span>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="border-t border-teal-100 p-3 space-y-3" data-testid="physio-fq-body">
          {figures.map((f, i) => (
            <div key={f.id || i} className="rounded-lg border border-teal-100 p-3">
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm" style={{ color: TEAL }}>{f.title}</span>
                  <span className="text-[10px] text-gray-400 uppercase">{f.type}</span>
                </div>
                <Badge grade={f.grade} />
              </div>
              {f.issues.length > 0 && (
                <div className="text-xs text-red-700 mb-1">
                  <strong>Critical:</strong>
                  <ul className="list-disc ml-5">{f.issues.map((m, j) => <li key={j}>{m}</li>)}</ul>
                </div>
              )}
              {f.warnings.length > 0 && (
                <div className="text-xs text-amber-700 mb-1">
                  <strong>Warnings:</strong>
                  <ul className="list-disc ml-5">{f.warnings.map((m, j) => <li key={j}>{m}</li>)}</ul>
                </div>
              )}
              {f.ok.length > 0 && (
                <div className="text-xs text-emerald-700">
                  <strong>Passes:</strong>
                  <ul className="list-disc ml-5">{f.ok.map((m, j) => <li key={j}>{m}</li>)}</ul>
                </div>
              )}
            </div>
          ))}
          <p className="text-[11px] text-gray-500 italic">
            Recharts figures render as vector SVG — effective DPI is unlimited. Labels &amp; legend compliance is checked automatically; re-export won't change resolution but can include/exclude figures flagged here.
          </p>
        </div>
      )}
    </div>
  );
}
